import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { useGraphql } from "~/api-client";
import type { CoordsInput, GetOrderingQuery } from "~/graphql-client/generated";
import type { OrderDispatchOption, TimeSlot } from "~/types";
import { CART_MAX_AGE_MINUTES } from "./cart";
import { useLocation } from "./locations";

export const DEFAULT_TIP_RATIOS = [0.1, 0.125, 0.15];

export function useLocationTimezone() {
  const { location } = useLocation();
  return location?.timezone;
}

interface HookResult {
  locationRef: string | null;
  loading: boolean;
  ordering: GetOrderingQuery["ordering"];
}

const orderingConfigContext = createContext<HookResult>({
  loading: true,
  ordering: null,
  locationRef: null,
});

function useOrderingConfig(locationRef: string) {
  const context = useContext(orderingConfigContext);
  if (locationRef !== context.locationRef) throw new Error("Invalid access");
  return context;
}

export function OrderingConfigProvider({
  locationRef,
  children,
}: {
  locationRef: string;
  children: ReactNode;
}) {
  const { ordering, loading } = _useOrderingConfig(locationRef);

  return (
    <orderingConfigContext.Provider
      value={{ locationRef, ordering: ordering ?? null, loading }}
    >
      {children}
    </orderingConfigContext.Provider>
  );
}

function _useOrderingConfig(locationRef: string) {
  const { getOrdering } = useGraphql();
  const { data, isLoading } = useQuery({
    queryKey: ["ordering-config", locationRef],
    queryFn: () => (locationRef ? getOrdering(locationRef) : null),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    staleTime: Infinity,
    refetchInterval: () => {
      // Got some options here
      // Could gradually back off
      // Or not refresh outside store hours, etc
      return 60 * 1000;
    },
  });

  return {
    ordering: data?.ordering,
    loading: isLoading,
  };
}

export function useDynamicDelivery(
  locationRef: string,
  to?: CoordsInput,
  dispatchOption?: OrderDispatchOption | null,
) {
  const { getDynamicDelivery } = useGraphql();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["dynamic-delivery", locationRef, to, dispatchOption],
    queryFn: () => {
      if (!locationRef || dispatchOption !== "DELIVERY") return null;
      return getDynamicDelivery(locationRef, to);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    staleTime: Infinity,
  });

  return {
    deliveryPrice: data?.ordering?.deliveryConfig?.feePrice || null,
    deliveryMinOrderAmount:
      data?.ordering?.deliveryConfig?.minimumAmount || null,
    deliveryFeeLoading: isLoading,
    reload: async () => {
      await refetch();
    },
  };
}

export function useTipsEnabled(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
): boolean {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchOption) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.tipsEnabled || false;
    case "PICK_UP":
      return ordering?.pickupConfig?.tipsEnabled || false;
    case "SERVICE":
      return ordering?.serviceConfig?.tipsEnabled || false;
    default:
      return false;
  }
}

export function useExternalPaymentEnabled(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
): boolean {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchOption) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.externalPaymentEnabled || false;
    case "PICK_UP":
      return ordering?.pickupConfig?.externalPaymentEnabled || false;
    case "SERVICE":
      return ordering?.serviceConfig?.externalPaymentEnabled || false;
    default:
      return false;
  }
}

export function useExternalPaymentLimit(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
): number | null {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchOption) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.externalPaymentLimit ?? null;
    case "PICK_UP":
      return ordering?.pickupConfig?.externalPaymentLimit ?? null;
    case "SERVICE":
      return ordering?.serviceConfig?.externalPaymentLimit ?? null;
    default:
      return null;
  }
}

export function useAvailableOrderDispatchOptions(locationRef: string) {
  const { ordering, loading } = useOrderingConfig(locationRef);
  const options: OrderDispatchOption[] = [];

  // In priority order, if customer hasn't got one selected
  // will pick the first, see getOrderDispatchOption
  if (ordering?.pickupConfig) {
    options.push("PICK_UP");
  }
  if (ordering?.deliveryConfig) {
    options.push("DELIVERY");
  }
  if (ordering?.serviceConfig) {
    options.push("SERVICE");
  }

  return {
    availableOrderDispatchOptions: options,
    loadingAvailableOrderDispatchOptions: loading,
  };
}

export function usePickupActive(
  locationRef: string,
  timeSlot: TimeSlot | null,
): boolean | undefined {
  const { ordering } = useOrderingConfig(locationRef);

  return (
    ordering?.pickupConfig?.active ||
    selectedDispatchTimeSlotIsAvailable(
      timeSlot,
      ordering?.pickupConfig?.timeSlots || [],
    )
  );
}

export function useDeliveryActive(
  locationRef: string,
  timeSlot: TimeSlot | null,
): boolean {
  const { ordering } = useOrderingConfig(locationRef);

  return (
    ordering?.deliveryConfig?.active ||
    selectedDispatchTimeSlotIsAvailable(
      timeSlot,
      ordering?.deliveryConfig?.timeSlots || [],
    )
  );
}

export function useServiceActive(locationRef: string): boolean {
  const { ordering } = useOrderingConfig(locationRef);

  return ordering?.serviceConfig?.active || false;
}

export function useOrderingActive(
  locationRef: string,
  timeSlot: TimeSlot | null,
) {
  const { loading } = useOrderingConfig(locationRef);
  const pickupActive = usePickupActive(locationRef, timeSlot);
  const deliveryActive = useDeliveryActive(locationRef, timeSlot);
  const serviceActive = useServiceActive(locationRef);

  return {
    deliveryActive,
    pickupActive,
    serviceActive,
    loadingOrdering: loading,
  };
}

function selectedDispatchTimeSlotIsAvailable(
  selectedTimeSlot: TimeSlot | null,
  availableTimeSlots: TimeSlot[],
) {
  if (!selectedTimeSlot) return false;

  const foundSlot = availableTimeSlots.find(
    (timeSlot) => timeSlot.ref === selectedTimeSlot.ref,
  );

  return Boolean(foundSlot?.available);
}

export function useTipRatios(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
): number[] {
  const { ordering } = useOrderingConfig(locationRef);
  let tipRatios: number[] | undefined;
  switch (dispatchOption) {
    case "DELIVERY":
      tipRatios = ordering?.deliveryConfig?.tipRatios || [];
      break;
    case "PICK_UP":
      tipRatios = ordering?.pickupConfig?.tipRatios || [];
      break;
    case "SERVICE":
      tipRatios = ordering?.serviceConfig?.tipRatios || [];
      break;
    default:
      tipRatios = [];
  }

  return tipRatios.length ? tipRatios : DEFAULT_TIP_RATIOS;
}

export function useServiceChargeRate(locationRef: string) {
  const { ordering, loading } = useOrderingConfig(locationRef);
  return {
    serviceChargeRate: ordering?.serviceConfig?.serviceChargeRate || 0,
    loadingServiceChargeRate: loading,
  };
}

export function useServiceChargeLabel(locationRef: string): string {
  const { ordering } = useOrderingConfig(locationRef);
  return ordering?.serviceConfig?.serviceChargeLabel || "Service Charge";
}

export function useTimeSlotsForDispatchType(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): TimeSlot[] | null {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchType) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.timeSlots || [];
    case "PICK_UP":
      return ordering?.pickupConfig?.timeSlots || [];
  }
  return [];
}

export function useTimeSlotsEnabled(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
) {
  const { ordering } = useOrderingConfig(locationRef);
  let timeSlotsEnabled = false;
  if (
    dispatchOption === "PICK_UP" &&
    ordering?.pickupConfig?.timeSlots?.length
  ) {
    timeSlotsEnabled = true;
  } else if (
    dispatchOption === "DELIVERY" &&
    ordering?.deliveryConfig?.timeSlots?.length
  ) {
    timeSlotsEnabled = true;
  }

  return timeSlotsEnabled;
}

export function useServiceValidRefs(
  locationRef: string,
): { ref: string }[] | null | undefined {
  const { ordering } = useOrderingConfig(locationRef);
  return ordering?.serviceConfig?.validRefs;
}

export function useWaitTimeForDispatchType(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): string | null {
  const { ordering } = useOrderingConfig(locationRef);

  switch (dispatchType) {
    case "DELIVERY": {
      if (!ordering?.deliveryConfig) {
        return null;
      }

      if (
        ordering.deliveryConfig.minutesMin === 0 &&
        ordering.deliveryConfig.minutesMax === 0
      ) {
        return null;
      }

      return `${ordering.deliveryConfig.minutesMin} to ${ordering.deliveryConfig.minutesMax} minutes`;
    }
    case "PICK_UP": {
      const minutes = ordering?.pickupConfig?.waitTime ?? null;
      if (!minutes) return null;
      return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    }
    case "SERVICE": {
      const minutes = ordering?.serviceConfig?.waitTime ?? null;
      if (!minutes) return null;
      return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    }
  }
  return null;
}

export function useAllowItemNotes(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): boolean | undefined {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchType) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.allowItemNotes;
    case "PICK_UP":
      return ordering?.pickupConfig?.allowItemNotes;
    case "SERVICE":
      return ordering?.serviceConfig?.allowItemNotes;
    default:
      break;
  }
}

export function useItemNotesMaxLength(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): number | undefined {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchType) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.itemNotesMaxLength || undefined;
    case "PICK_UP":
      return ordering?.pickupConfig?.itemNotesMaxLength || undefined;
    case "SERVICE":
      return ordering?.serviceConfig?.itemNotesMaxLength || undefined;
    default:
      break;
  }
}

export function useAllowOrderNotes(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): boolean {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchType) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.allowOrderNotes || false;
    case "PICK_UP":
      return ordering?.pickupConfig?.allowOrderNotes || false;
    case "SERVICE":
      return ordering?.serviceConfig?.allowOrderNotes || false;
    default:
      return false;
  }
}

export function useOrderNotesMaxLength(
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): number | undefined {
  const { ordering } = useOrderingConfig(locationRef);
  switch (dispatchType) {
    case "DELIVERY":
      return ordering?.deliveryConfig?.orderNotesMaxLength || undefined;
    case "PICK_UP":
      return ordering?.pickupConfig?.orderNotesMaxLength || undefined;
    case "SERVICE":
      return ordering?.serviceConfig?.orderNotesMaxLength || undefined;
    default:
      return undefined;
  }
}

export function useTipDefault(
  locationRef: string,
  baseAmount: number,
  fulfillmentType: OrderDispatchOption | null,
): {
  loading: boolean;
  defaultTipAmount: number;
} {
  const { ordering, loading } = useOrderingConfig(locationRef);
  const tipRatios = useTipRatios(locationRef, fulfillmentType);

  let config:
    | null
    | undefined
    | { tipRatios?: number[]; tipDefaultIndex?: null | number };

  switch (fulfillmentType) {
    case "PICK_UP": {
      config = ordering?.pickupConfig;
      break;
    }

    case "DELIVERY": {
      config = ordering?.deliveryConfig;
      break;
    }

    case "SERVICE": {
      config = ordering?.serviceConfig;
      break;
    }
  }

  // allow tipDefaultIndex as 0 or more
  if (
    config?.tipDefaultIndex === null ||
    config?.tipDefaultIndex === undefined
  ) {
    return { loading, defaultTipAmount: 0 };
  }

  return {
    loading,
    defaultTipAmount: Math.round(
      baseAmount * (tipRatios[config.tipDefaultIndex] || 0),
    ),
  };
}

export function useEarliestOrderTime(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
) {
  const { ordering, loading } = useOrderingConfig(locationRef);

  let minutes = 0;

  if (dispatchOption === "DELIVERY" && ordering?.deliveryConfig?.minutesMin) {
    minutes = ordering.deliveryConfig.minutesMin;
  }

  if (dispatchOption === "PICK_UP" && ordering?.pickupConfig?.waitTime) {
    minutes = ordering.pickupConfig.waitTime;
  }

  // Add some wiggle room to allow the cart checking to do it's thing
  minutes = minutes + CART_MAX_AGE_MINUTES;

  const now = new Date();
  const earliestOrderTime = new Date();

  earliestOrderTime.setMinutes(now.getMinutes() + minutes);

  return {
    // never return a time in the past
    earliestOrderTime: earliestOrderTime < now ? now : earliestOrderTime,
    loadingEarliestOrderTime: loading,
  };
}
